import React, { ReactElement, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { CognitoUser } from '@aws-amplify/auth';
import { oAuthSignIn, signOut } from '../../utils/auth-functions';
import { checkAuthCookies, setAuthCookies } from '../../utils/cookies-management';
import { registerFunction } from '../../utils/app-bridge';
import { OAuthComponents, LoginForm } from '../organisms';
import CardHeader from '../organisms/CardHeader';
import useReturnUrl from '../../hooks/useReturnUrl';

export interface CognitoUserAttributes {
  name?: string;
  family_name?: string;
  nickname?: string;
  email?: string;
}

type CustomCognitoUser = CognitoUser & { attributes: CognitoUserAttributes };

export default function Login(): ReactElement {
  const { search } = useLocation();
  const queryString = new URLSearchParams(search);
  const redirectUrl = queryString.get('redirectUrl');
  const inApp = queryString.get('inApp');
  const platform = queryString.get('platform');
  const confirmedUser = queryString.get('confirmedUser');
  const [profile, setProfile] = useState<CustomCognitoUser | string | undefined>(undefined);
  const history = useHistory();
  const returnUrl = useReturnUrl();

  registerFunction('OAuthLogin', oAuthSignIn);
  const withSearchParams = (base: string) => {
    const params = new URLSearchParams({
      inApp: String(inApp === 'true'),
      ...(redirectUrl ? { redirectUrl } : {}),
    });
    return `${base}?${params.toString()}`;
  };

  const registerLink = withSearchParams('/register');
  const forgotPasswordLink = withSearchParams('/forgot-password');
  useEffect(() => {
    const redirectLogout = Cookies.get('redirectLogout');
    if (redirectLogout && redirectLogout !== '') {
      window.location.href = redirectLogout;
    }
    Cookies.remove('redirectLogout');
    if (platform) {
      Cookies.set('platform', platform);
    }
  }, []);

  useEffect(() => {
    signOut();
    setProfile('The user is not authenticated');
  }, []);

  useEffect(() => {
    if (profile && profile !== 'The user is not authenticated') {
      const areCookiesSet = checkAuthCookies();
      if (!areCookiesSet && typeof profile !== 'string') {
        const tokens = {
          accessToken: {
            jwtToken: profile.getSignInUserSession()?.getAccessToken().getJwtToken() || '',
          },
          idToken: {
            jwtToken: profile.getSignInUserSession()?.getIdToken().getJwtToken() || '',
          },
          refreshToken: profile.getSignInUserSession()?.getRefreshToken().getToken(),
        };
        setAuthCookies(tokens);
      }
      const goto = redirectUrl || returnUrl;

      if (goto) window.location.href = goto;
      else history.replace('/profile');
    } else if (profile === 'The user is not authenticated') {
      signOut();
    }
  }, [profile]);

  return (
    <div className="h-100">
      <div className="d-flex align-items-center h-100">
        <div className="mx-auto login-box">
          <div className="card">
            <CardHeader />
            <div className="card-body">
              <h1 className="card-title text-center py-1">Accedi</h1>
              <p className="card-text">{confirmedUser === 'true' ? "Conferma dell'account effettuata. Inserisci le tue credenziali per proseguire!" : 'Entra con il tuo account o creane uno per poter leggere i tuoi albi preferiti con tutti i tuoi device'}</p>
              <LoginForm inApp={inApp} redirectUrl={redirectUrl || undefined} />
              <Link className="lost-password" rel="keep-params" to={forgotPasswordLink}>
                Hai dimenticato la password?
              </Link>
              <div className="social-login text-center">
                <span>
                  <Link rel="keep-params" to={registerLink}>
                    Registrati
                  </Link>
                  &nbsp;oppure accedi con{' '}
                </span>
                <OAuthComponents />
              </div>
              {!inApp && (
                <div className="subscribe-now text-center">
                  <p>
                    Non sei ancora abbonato?
                    <span>
                      <button className="btn link-override" onClick={() => (window.location.href = process.env.REACT_APP_SBE_SHOP_URL || '')}>
                        <u>Abbonati</u>
                      </button>
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
